import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Button } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classNames from "classnames";
import _ from "lodash";
import GradesProvider from "AppComponents/SharedEntities/Grades/GradesProvider";
import CoursesProvider from "AppComponents/SharedEntities/Courses/CoursesProvider";
import SelectParticipantsProvider, {
  useSelectParticipantsContext,
} from "./context/SelectParticipantsContext";
import { ACTIVE_MODE } from "./modules/SelectParticipantsConstants";
import LeftPanel from "./components/LeftPanel";
import classes from "./SelectParticipantsModal.scss";
import Toolbar from "./components/Toolbar/Toolbar";

const MODAL_BODY_STYLE = {
  padding: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "row",
  height: "100%",
};

const MODAL_FOOTER_STYLE = {
  padding: "16px 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const SelectParticipantsInner = ({
  isOpen,
  onClose,
  onConfirm,
  isRecipientSelected,
  testId,
}) => {
  const { t } = useI18n(["calendar", "common"]);
  const {
    selectedGradeList,
    selectedCourseList,
    activeModeSelectedCount,
    isAllEmpty,
    handleClearActiveMode,
    handleResetToInitial,
    activeMode,
    effectiveSelectedMemberIds,
    excludedMemberIds,
  } = useSelectParticipantsContext();
  const isCustomEntitiesMode = activeMode === ACTIVE_MODE.MEMBERS;
  const componentTestId = testId || "selectParticipantsModal";

  const handleClose = useCallback(() => {
    handleResetToInitial();
    onClose();
  }, [handleResetToInitial, onClose]);

  const handleConfirm = useCallback(() => {
    const payload = {
      selectedGradeList,
      selectedCourseList,
      selectedMemberList: [],
      excludedMemberIds: {},
    };
    if (isCustomEntitiesMode) {
      payload.selectedMemberIds = effectiveSelectedMemberIds;
      payload.excludedMemberIds = excludedMemberIds;
      payload.effectiveSelectedMemberIds = effectiveSelectedMemberIds;
    }
    onConfirm(payload);
    onClose();
  }, [
    onConfirm,
    onClose,
    selectedGradeList,
    selectedCourseList,
    isCustomEntitiesMode,
    effectiveSelectedMemberIds,
    excludedMemberIds,
  ]);

  const activeModeClearDisabled =
    activeMode === ACTIVE_MODE.GRADES
      ? _.isEmpty(selectedGradeList)
      : _.isEmpty(selectedCourseList);

  const selectedCountLabel =
    activeMode === ACTIVE_MODE.GRADES
      ? t("common:grade_selected_with_count", {
          count: activeModeSelectedCount,
        })
      : t("common:selected_course_count", {
          count: activeModeSelectedCount,
        });

  return (
    <Modal
      dsVersion="2.0"
      isOpen={isOpen}
      onClose={handleClose}
      width={isCustomEntitiesMode ? "960px" : "544px"}
      height="680px"
    >
      <Modal.Header
        title={t("calendar:select_participants_to_invite")}
        testId={`${componentTestId}-header`}
      />
      <Modal.Body style={MODAL_BODY_STYLE}>
        <div
          className={classNames(
            isCustomEntitiesMode
              ? classes.leftPanelWithRightPanel
              : classes.leftPanel
          )}
        >
          <Toolbar testId={componentTestId} />
          <LeftPanel testId={componentTestId} />
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator style={MODAL_FOOTER_STYLE}>
        <div className={classes.footerLeft}>
          <Button
            dsVersion="2.0"
            variant="progressive"
            type="inline"
            testId={`${componentTestId}-selectedCount`}
          >
            {selectedCountLabel}
          </Button>
        </div>
        <div className={classes.footerRight}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            onClick={handleClearActiveMode}
            disabled={activeModeClearDisabled}
            testId={`${componentTestId}-clear`}
          >
            {t("common:clear")}
          </Button>
          <Button
            dsVersion="2.0"
            variant="progressive"
            type="fill"
            disabled={isAllEmpty}
            onClick={handleConfirm}
            testId={`${componentTestId}-confirm`}
          >
            {isRecipientSelected || !isAllEmpty
              ? t("common:update")
              : t("common:select")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

SelectParticipantsInner.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isRecipientSelected: PropTypes.bool,
  testId: PropTypes.string,
};

SelectParticipantsInner.defaultProps = {
  isRecipientSelected: false,
  testId: undefined,
};

const GradesProviderBridge = ({
  organizationId,
  academicYearId,
  selectedUserTypes,
  children,
}) => {
  const {
    activeMode,
    selectedGradeList,
    setSelectedGradeList,
    searchText,
    setSearchText,
    selectedSchoolIds,
    setSelectedSchoolIds,
  } = useSelectParticipantsContext();

  return (
    <GradesProvider
      organizationId={organizationId}
      academicYearId={academicYearId}
      selectedUserTypes={selectedUserTypes}
      viewOnly={false}
      isActive={activeMode === ACTIVE_MODE.GRADES}
      selectedGradeList={selectedGradeList}
      setSelectedGradeList={setSelectedGradeList}
      searchText={searchText}
      setSearchText={setSearchText}
      selectedSchoolIds={selectedSchoolIds}
      setSelectedSchoolIds={setSelectedSchoolIds}
    >
      {children}
    </GradesProvider>
  );
};

GradesProviderBridge.propTypes = {
  organizationId: PropTypes.string.isRequired,
  academicYearId: PropTypes.string,
  selectedUserTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
};

GradesProviderBridge.defaultProps = {
  academicYearId: undefined,
};

const CoursesProviderBridge = ({
  organizationId,
  academicYearId,
  selectedUserTypes,
  children,
}) => {
  const {
    activeMode,
    selectedCourseList,
    setSelectedCourseList,
    searchText,
    setSearchText,
    selectedSchoolIds,
    setSelectedSchoolIds,
  } = useSelectParticipantsContext();

  return (
    <CoursesProvider
      organizationId={organizationId}
      academicYearId={academicYearId}
      selectedUserTypes={selectedUserTypes}
      viewOnly={false}
      isActive={activeMode === ACTIVE_MODE.CLASSES}
      selectedCourseList={selectedCourseList}
      setSelectedCourseList={setSelectedCourseList}
      searchText={searchText}
      setSearchText={setSearchText}
      selectedSchoolIds={selectedSchoolIds}
      setSelectedSchoolIds={setSelectedSchoolIds}
    >
      {children}
    </CoursesProvider>
  );
};

CoursesProviderBridge.propTypes = {
  organizationId: PropTypes.string.isRequired,
  academicYearId: PropTypes.string,
  selectedUserTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
};

CoursesProviderBridge.defaultProps = {
  academicYearId: undefined,
};

const SelectParticipantsModal = ({
  isOpen,
  onClose,
  onConfirm,
  organizationId,
  academicYearId,
  selectedUserTypes,
  initialSelectedGradeList,
  initialSelectedCourseList,
  isRecipientSelected,
  testId,
  activeMode,
  enableMembersMode,
}) => {
  return (
    <SelectParticipantsProvider
      initialSelectedGradeList={initialSelectedGradeList}
      initialSelectedCourseList={initialSelectedCourseList}
      activeMode={activeMode}
      enableMembersMode={enableMembersMode}
    >
      <GradesProviderBridge
        organizationId={organizationId}
        academicYearId={academicYearId}
        selectedUserTypes={selectedUserTypes}
      >
        <CoursesProviderBridge
          organizationId={organizationId}
          academicYearId={academicYearId}
          selectedUserTypes={selectedUserTypes}
        >
          <SelectParticipantsInner
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onConfirm}
            isRecipientSelected={isRecipientSelected}
            testId={testId}
          />
        </CoursesProviderBridge>
      </GradesProviderBridge>
    </SelectParticipantsProvider>
  );
};

const DEFAULT_USER_TYPES = ["STAFF", "STUDENT", "FAMILY_MEMBER"];

SelectParticipantsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  academicYearId: PropTypes.string,
  selectedUserTypes: PropTypes.arrayOf(PropTypes.string),
  initialSelectedGradeList: PropTypes.array,
  initialSelectedCourseList: PropTypes.array,
  initialSelectedMemberList: PropTypes.array,
  isRecipientSelected: PropTypes.bool,
  testId: PropTypes.string,
  activeMode: PropTypes.string.isRequired,
  enableMembersMode: PropTypes.bool,
};

SelectParticipantsModal.defaultProps = {
  academicYearId: undefined,
  selectedUserTypes: DEFAULT_USER_TYPES,
  initialSelectedGradeList: [],
  initialSelectedCourseList: [],
  initialSelectedMemberList: [],
  isRecipientSelected: false,
  testId: undefined,
  enableMembersMode: false,
};

export default SelectParticipantsModal;
